import axios from 'axios'
import { parseSync, stringifySync } from 'subtitle'
import { defineStore } from 'pinia'
import { useCommonStore } from './CommonStore.js';
import { usePodTranscriptStore } from './PodTranscriptStore.js';
import { usePodLabelStore } from './PodLabelStore.js';
// import Api from '../api/Api'

export const usePlayerStore = defineStore('player', {
  state: () => ({
    isPlaying: false,
    isSeeking: false,
    duration: 1,
    volume: 1,
    muted: false,
    speed: 1,
    fullscreen: false,

    currentTime: 0,
    seekTimeEvent: null,
    pauseEvent: null,

    activeSubtitle: null,
    vttSubtitles: [],
    srtSubtitles: [],
    activeAudioDescription: null,
    audioDescriptions: [],

    activeLayer: 0,
    layersOptions: {
      COMMENTS: {},
      AWSDETECTEDTEXT: {},
      AWSDETECTEDLABELS: {},
    },
  }),
  getters: {
    getSeekTimeEvent: function(state) {
      return state.seekTimeEvent;
    },
    getPauseEvent: function(state) {
      return state.pauseEvent;
    },
    getActiveSubtitle: function(state) {
      const podTranscriptStore = usePodTranscriptStore();
      const dynamicVTTFiles = podTranscriptStore.getDynamicVTTFiles;

      for (let i = 0; i < dynamicVTTFiles.length; i++) {
        if (
          state.activeSubtitle &&
          state.activeSubtitle.isTranscript &&
          state.activeSubtitle.transcriptId == dynamicVTTFiles[i].transcriptId &&
          state.activeSubtitle.url != dynamicVTTFiles[i].url
        ) {
          return dynamicVTTFiles[i];
        }
      }

      return state.activeSubtitle;
    },
    availableSubtitles: function(state) {
      let availableSubs = [];

      availableSubs = availableSubs.concat(state.vttSubtitles).concat(state.srtSubtitles);

      availableSubs.sort(function(a, b) {
        return a.label.localeCompare(b.label);
      });

      const podTranscriptStore = usePodTranscriptStore();
      const dynamicVTTFiles = podTranscriptStore.getDynamicVTTFiles;

      for (let i = 0; i < dynamicVTTFiles.length; i++) {
        availableSubs.push(dynamicVTTFiles[i])
      }

      if (availableSubs.length > 0) {
        availableSubs.push({label: "Off", language: 'en', url: null});
      }

      // console.log("Available subs", availableSubs);
      return availableSubs;
    },
    availableAudioDescriptions: function(state) {
      const availableAudioDescriptions = [...state.audioDescriptions].sort(function(a, b) {
        return a.label.localeCompare(b.label);
      });

      if (availableAudioDescriptions.length > 0) {
        availableAudioDescriptions.push({label: "Off", language: 'en', url: null});
      }

      return availableAudioDescriptions;
    },
    getLayersOptions: function(state) {
      return state.layersOptions;
    },
    getActiveLayer: function(state) {
      return state.activeLayer;
    },
    getAvailableLayers: function() {
      let availableLayers = {};

      // TODO: filter the ML labels by type - text, label, face...

      // const GETCOMMENTS = rootGetters.keywords.POD.COMMENT.GETCOMMENTS;
      // Uncomment this to enable comments layer
      // if (rootGetters[GETCOMMENTS]) {
      //   availableLayers.COMMENTS = 1;
      // }

      const podLabelStore = usePodLabelStore();

      const mlLabels = podLabelStore.getMlLabels || [];

      if (mlLabels.find(x => x.service == 'aws-detecting-text')) {
        availableLayers.AWSDETECTEDTEXT = 2;
      }

      if (mlLabels.find(x => x.service == 'aws-detecting-labels')) {
        availableLayers.AWSDETECTEDLABELS = 3;
      }

      return availableLayers;
    }
  },
  actions: {
    setIsPlaying: function (value) {
      this.isPlaying = value;
    },
    setIsSeeking: function (value) {
      this.isSeeking = value;
    },
    setDuration: function(duration) {
      this.duration = duration;
    },
    setVolume: function(volume) {
      this.volume = volume;
    },
    setMuted: function(muted) {
      this.muted = muted;
    },
    setSpeed: function(speed) {
      this.speed = speed;
    },
    setFullscreen: function(fullscreen) {
      this.fullscreen = fullscreen;
    },
    setCurrentTime: function(time) {
      this.currentTime = time;
    },
    setSeekTimeEvent: function(time) {
      this.seekTimeEvent = time;
    },
    setPauseEvent: function(time) {
      this.pauseEvent = time;
    },
    setActiveSubtitle: function(subtitle) {
      this.activeSubtitle = subtitle && subtitle.url ? subtitle : null;
    },
    setActiveAudioDescription: function(audioDescription) {
      this.activeAudioDescription = audioDescription && audioDescription.url ? audioDescription : null;
    },
    resetPlayer: function() {
      const wasMuted = this.muted;
      const prevVolume = this.volume;

      this.isPlaying = false;
      this.isSeeking = false;
      this.duration = 1;
      this.volume = prevVolume;
      this.muted = wasMuted;
      this.fullscreen = false;
      this.currentTime = 0;
      this.seekTimeEvent = null;
      this.pauseEvent = null;
      this.activeSubtitle = null;
      this.activeAudioDescription = null;
      this.activeLayer = 0;
    },
    setVTTSubtitles: function(subtitles) {
      this.vttSubtitles = subtitles;
    },
    setSRTSubtitles: function(subtitles) {
      // revoke old objects to avoid memory leaks
      for (let i = 0; i < this.srtSubtitles.length; i++) {
        URL.revokeObjectURL(this.srtSubtitles[i].url);
      }

      this.srtSubtitles = subtitles;
    },
    refreshActiveSubtitle: function() {
      if (!this.activeSubtitle) return;

      if (this.activeSubtitle.id) {
        let subtitleAssets = [].concat(this.vttSubtitles).concat(this.srtSubtitles);
        this.activeSubtitle = subtitleAssets.find(x => x.id == this.activeSubtitle.id);
        return;
      }

      if (this.activeSubtitle.isTranscript) {
        // handled in the getter getActiveSubtitle
        return;
      }

      this.activeSubtitle = null;
    },
    setAudioDescriptions: function(audioDescriptions) {
      this.audioDescriptions = audioDescriptions;
    },
    refreshActiveAudioDescription: function() {
      if (!this.activeAudioDescription) return;

      this.activeAudioDescription = this.audioDescriptions.find((asset) => asset.id == this.activeAudioDescription.id) || null;
    },
    setActiveLayer: function(layer) {
      if (this.activeLayer === layer) {
        this.activeLayer = 0;
      } else {
        this.activeLayer = layer;
      }
    },
    setLayersOptions: function(options) {
      for (let layerName in this.layersOptions) {
        if (this.layersOptions[layerName] &&
            options[layerName] &&
            typeof this.layersOptions[layerName] == 'object' &&
            typeof options[layerName] == 'object'
        ) {
          this.layersOptions[layerName] = options[layerName];
        }
      }
    },



    refreshSubtitles: async function(pod) {
      // console.log("PlayerStore: Refresh subtitles action", pod);

      let prom1 = this.refreshVTTSubtitles(pod);
      let prom2 = this.refreshSRTSubtitles(pod);

      await Promise.all([prom1, prom2]);
      this.refreshActiveSubtitle();
    },

    refreshAudioDescriptions: function(pod) {
      const commonStore = useCommonStore();
      const STATECOMPLETE = commonStore.assetStates.COMPLETE;

      const audioDescriptions = [];

      if (pod && pod.asset && Array.isArray(pod.asset.audiodescription)) {
        for (let i = 0; i < pod.asset.audiodescription.length; i++) {
          const asset = pod.asset.audiodescription[i];

          if (asset.broken || !asset.url || asset.state !== STATECOMPLETE) {
            continue;
          }

          audioDescriptions.push({
            id: asset.id,
            label: asset.title || asset.name || asset.filename || `Audio description ${i + 1}`,
            language: asset.language || 'en',
            url: asset.url,
          });
        }
      }

      this.setAudioDescriptions(audioDescriptions);
      this.refreshActiveAudioDescription();
    },

    refreshVTTSubtitles: function(pod) {
      const commonStore = useCommonStore();
      const STATECOMPLETE = commonStore.assetStates.COMPLETE;

      let vttSubtitles = [];

      if (pod && pod.asset && pod.asset.vtt && pod.asset.vtt.length > 0) {
        for (let i = 0; i < pod.asset.vtt.length; i++) {
          if (pod.asset.vtt[i].state !== STATECOMPLETE) {
            continue;
          }

          let sub = {
            id: pod.asset.vtt[i].id,
            label: pod.asset.vtt[i].name,
            language: 'en',   // what else?
            url: pod.asset.vtt[i].url
          };
          vttSubtitles.push(sub);   // maybe do some kind of validation
        }
      }

      this.setVTTSubtitles(vttSubtitles);
    },
    refreshSRTSubtitles: async function(pod) {
      const commonStore = useCommonStore();
      const STATECOMPLETE = commonStore.assetStates.COMPLETE;

      let srtSubtitles = [];
      let promises = [];

      if (pod && pod.asset && pod.asset.srt && pod.asset.srt.length > 0) {
        for (let i = 0; i < pod.asset.srt.length; i++) {
          if (!pod.asset.srt[i].url || pod.asset.srt[i].state !== STATECOMPLETE) {
            promises.push(new Promise(function(resolve) { return resolve(false); }));
            continue;
          }
          promises.push(axios.get(pod.asset.srt[i].url));
        }
      }

      // get the content of the SRT files and convert it to VTT
      let results = await Promise.all(promises);

      if (pod && pod.asset && pod.asset.srt && pod.asset.srt.length > 0) {
        for (let p = 0; p < pod.asset.srt.length; p++) {
          if (!results[p] || results[p].data === undefined)
            continue;
          let cuez = parseSync(results[p].data);
          let vttData = stringifySync(cuez, {format: 'WebVTT'});

          let blob = new Blob([vttData], {type: 'text/vtt'});
          let url = URL.createObjectURL(blob);

          let sub = {
            id: pod.asset.srt[p].id,
            label: pod.asset.srt[p].name,
            language: 'en',   // what else?
            url: url
          };
          srtSubtitles.push(sub);
        }
      }

     this.setSRTSubtitles(srtSubtitles);
    },
  },
});
